import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

export const useMessageNotifications = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [currentProfileId, setCurrentProfileId] = useState<string | null>(null);
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission>('default');

  // Extraire l'ID de la conversation de l'URL
  const currentConversationId = location.pathname.match(/\/messages\/([^/]+)/)?.[1] || null;

  // Demander la permission pour les notifications
  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      console.log('Ce navigateur ne supporte pas les notifications');
      return;
    }

    // Si déjà autorisé, ne rien faire
    if (Notification.permission === 'granted') {
      setNotificationPermission('granted');
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      console.log('Permission notification:', permission);
      setNotificationPermission(permission);
    } catch (error) {
      console.error('Erreur lors de la demande de permission:', error);
    }
  };

  // Créer une notification du navigateur
  const createBrowserNotification = (title: string, body: string, conversationId: string) => {
    if (notificationPermission !== 'granted') return;

    try {
      const notification = new Notification(title, {
        body,
        icon: '/icon.png',
        badge: '/icon.png',
        tag: `message-${conversationId}`,
        requireInteraction: true
      });

      notification.onclick = () => {
        window.focus();
        window.location.href = `/messages/${conversationId}`;
        notification.close();
      };

      console.log('Notification créée:', notification);
    } catch (error) {
      console.error('Erreur lors de la création de la notification:', error);
    }
  };

  useEffect(() => {
    requestNotificationPermission();
  }, []);

  // Récupérer l'ID du profil au chargement
  useEffect(() => {
    if (!user) return;

    const fetchProfileId = async () => {
      const { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (profile) {
        setCurrentProfileId(profile.id);
      }
    };

    fetchProfileId();
  }, [user]);

  useEffect(() => {
    if (!user || !currentProfileId) return;

    const channel = supabase
      .channel('message_notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'private_messages',
        },
        async (payload) => {
          const isInConversation = currentConversationId && 
            payload.new.conversation_id === currentConversationId;

          if (isInConversation || payload.new.sender_id === currentProfileId) {
            return;
          }

          const { data: sender } = await supabase
            .from('profiles')
            .select('username')
            .eq('id', payload.new.sender_id)
            .single();

          if (!sender) return;

          // Supprimer les anciennes notifications non lues pour cette conversation
          await supabase
            .from('notifications')
            .delete()
            .match({
              user_id: user.id,
              conversation_id: payload.new.conversation_id,
              type: 'new_message',
              read: false
            });

          // Créer la nouvelle notification
          await supabase
            .from('notifications')
            .insert({
              user_id: user.id,
              title: 'Nouveau message',
              body: `${sender.username} vous a envoyé un message`,
              type: 'new_message',
              conversation_id: payload.new.conversation_id,
              read: false
            });

          // Afficher une notification du navigateur si autorisé
          if (notificationPermission === 'granted' && document.visibilityState !== 'visible') {
            createBrowserNotification(
              'Nouveau message',
              `${sender.username} vous a envoyé un message`,
              payload.new.conversation_id
            );
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, currentProfileId, currentConversationId, notificationPermission]);
}; 